// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * What the user leaves with: a runnable command line, an Ollama Modelfile and
 * a machine-readable report.
 *
 * The flags are checked against llama.cpp's current `common/arg.cpp`, which
 * matters more than it sounds: `--no-mmap` and `--mlock` are deprecated in
 * favour of `--load-mode`, `-fa` now takes `on|off|auto`, and — the one that
 * actually breaks people — a quantised V cache is a **fatal error** without
 * flash attention (`quantized V cache requires flash_attn to be enabled`). A
 * command line that emits `-ctv q8_0` without `-fa on` does not start.
 */

import { shellQuote } from './format';
import type { Geometry } from './geometry';
import type { FitResult, KvSummary, WeightSummary } from './vram';

export interface CommandOptions {
  /** Local filename or the path the user will save the model to. */
  modelPath: string;
  /** `owner/repo` when the model came from Hugging Face. */
  hfRepo?: string;
  /** The quant label for the `-hf repo:QUANT` shorthand. */
  hfQuant?: string;
  nCtx: number;
  ngl: number;
  nLayer: number;
  cacheTypeK: string;
  cacheTypeV: string;
}

export interface CommandResult {
  command: string;
  /** Notes the UI shows beneath the command, in order. */
  notes: string[];
  /** True when flash attention had to be forced on for a quantised cache. */
  forcedFlashAttention: boolean;
}

const QUANTISED_CACHE = /^q/;

export function buildLlamaServerCommand(opts: CommandOptions): CommandResult {
  const notes: string[] = [];
  const args: string[] = [];

  if (opts.hfRepo && opts.hfQuant) {
    args.push('-hf', `${opts.hfRepo}:${opts.hfQuant}`);
    notes.push('The -hf form downloads and caches the model for you — no manual download step.');
  } else {
    args.push('-m', shellQuote(opts.modelPath));
  }

  args.push('-c', String(opts.nCtx));
  args.push('-ngl', opts.ngl > opts.nLayer ? 'all' : String(opts.ngl));

  const kQuantised = QUANTISED_CACHE.test(opts.cacheTypeK);
  const vQuantised = QUANTISED_CACHE.test(opts.cacheTypeV);
  const forcedFlashAttention = kQuantised || vQuantised;

  if (forcedFlashAttention) {
    args.push('-fa', 'on');
    notes.push(
      'A quantised KV cache requires flash attention: llama.cpp exits with "quantized V cache requires flash_attn to be enabled" without it, so -fa on is not optional here.',
    );
  }
  if (opts.cacheTypeK !== 'f16') args.push('-ctk', opts.cacheTypeK);
  if (opts.cacheTypeV !== 'f16') args.push('-ctv', opts.cacheTypeV);

  args.push('--host', '127.0.0.1', '--port', '8080');

  if (opts.ngl <= opts.nLayer) {
    notes.push(
      `Only ${opts.ngl} of ${opts.nLayer} layers fit, so the rest run on the CPU. Expect a large slowdown — this is the point at which a smaller quantisation is usually the better trade.`,
    );
  }

  return { command: `llama-server ${args.join(' ')}`, notes, forcedFlashAttention };
}

export interface ModelfileOptions {
  modelPath: string;
  nCtx: number;
  ngl: number;
  nLayer: number;
  hasChatTemplate: boolean;
}

export function buildModelfile(opts: ModelfileOptions): { text: string; notes: string[] } {
  const lines = [
    `FROM ${opts.modelPath}`,
    '',
    `PARAMETER num_ctx ${opts.nCtx}`,
    `PARAMETER num_gpu ${opts.ngl > opts.nLayer ? opts.nLayer + 1 : opts.ngl}`,
  ];

  const notes = [
    'Build it with: ollama create my-model -f ./Modelfile',
    // Worth stating precisely, because the widely-repeated "Ollama defaults to
    // 2048" is no longer true: the default is negotiated from total VRAM
    // (262144 at 47 GiB or more, 32768 at 23 GiB or more, otherwise 4096),
    // clamped to the model's trained context, then stepped down if the load
    // runs out of memory. So the context you actually get depends on the
    // machine — setting num_ctx explicitly is the only way to pin it.
    "Ollama picks num_ctx from the VRAM it finds and quietly steps it down if the model does not fit, so the context you get varies by machine. An explicit PARAMETER num_ctx is the only way to pin it.",
  ];

  if (opts.hasChatTemplate) {
    notes.push(
      "This model's chat template is Jinja, and Ollama's TEMPLATE directive takes Go templates — the two are not interchangeable, so the template cannot simply be pasted in. Ollama tries to recognise known templates on import; run a short chat after ollama create and check the replies are not malformed before trusting it.",
    );
  }

  return { text: lines.join('\n'), notes };
}

// ── JSON report ──────────────────────────────────────────────────────

export interface ReportInput {
  label: string;
  source: string;
  geo: Geometry;
  weights: WeightSummary;
  kv: KvSummary;
  fit: FitResult;
  fileBytes: number | null;
  bytesRead: number;
  nCtx: number;
  cacheTypeK: string;
  cacheTypeV: string;
  ngl: number;
  fileType?: string;
  reconciliation: Reconciliation;
  generatedAt: string;
}

export function buildReportJson(r: ReportInput): string {
  return JSON.stringify(
    {
      tool: 'ggufscope',
      url: 'https://ggufscope.benrichardson.dev',
      generated: r.generatedAt,
      model: {
        label: r.label,
        source: r.source,
        name: r.geo.name,
        architecture: r.geo.arch,
        fileType: r.fileType,
        fileBytes: r.fileBytes,
        headerBytesRead: r.bytesRead,
      },
      geometry: {
        layers: r.geo.nLayer,
        embeddingLength: r.geo.nEmbd,
        headCount: r.geo.nHead,
        headCountKv: r.geo.nHeadKv[0],
        headCountKvPerLayer: new Set(r.geo.nHeadKv).size > 1 ? r.geo.nHeadKv : undefined,
        keyLength: r.geo.headK,
        valueLength: r.geo.headV,
        trainedContext: r.geo.ctxTrain,
        vocab: r.geo.vocab,
        expertCount: r.geo.expertCount,
        slidingWindow: r.geo.slidingWindow,
      },
      weights: {
        totalBytes: r.weights.totalBytes,
        parameters: r.weights.params,
        bitsPerWeight: Number(r.weights.bpw.toFixed(4)),
        quantMix: r.weights.byType.map((t) => ({
          type: t.name,
          tensors: t.count,
          bytes: t.bytes,
          bitsPerWeight: t.bitsPerWeight,
        })),
        unsizeable: r.weights.unsizeable,
      },
      kvCache: {
        requestedContext: r.nCtx,
        allocatedContext: r.kv.paddedCtx,
        cacheTypeK: r.cacheTypeK,
        cacheTypeV: r.cacheTypeV,
        totalBytes: r.kv.totalBytes,
        keyBytes: r.kv.keyBytes,
        valueBytes: r.kv.valueBytes,
        bytesPerToken: Math.round(r.kv.bytesPerToken),
      },
      fit: {
        verdict: r.fit.verdict,
        gpuMemoryBytes: r.fit.vramBytes,
        requiredBytes: r.fit.requiredBytes,
        weightBytesOnGpu: r.fit.weightBytesGpu,
        kvBytesOnGpu: r.fit.kvBytesGpu,
        workingMemoryAllowanceBytes: r.fit.reserveBytes,
        headroomBytes: r.fit.headroomBytes,
        gpuLayers: r.ngl,
        layersOnGpu: r.fit.plan.layersOnGpu,
        outputOnGpu: r.fit.plan.outputOnGpu,
      },
      verification: {
        computedFileBytes: r.reconciliation.expected,
        actualFileBytes: r.reconciliation.actual,
        deltaBytes: r.reconciliation.delta,
        status: r.reconciliation.status,
      },
      assumptions: r.geo.assumptions,
      cautions: r.geo.cautions,
      disclaimer:
        'Weight and KV-cache figures are computed from the file\'s own tensor table using llama.cpp\'s block sizes and cache formula. The working-memory allowance is a deliberate allowance, not a computed value — llama.cpp\'s compute buffer has no closed form.',
    },
    null,
    2,
  );
}

// ── The self-check ───────────────────────────────────────────────────

export type ReconciliationStatus = 'verified' | 'header-only' | 'truncated' | 'mismatch' | 'unknown';

export interface Reconciliation {
  status: ReconciliationStatus;
  expected: number;
  actual: number | null;
  delta: number | null;
  message: string;
}

/**
 * Compare the size implied by the parsed tensor table against the file's real
 * size. This is the single most valuable assertion in the tool: if the sum of
 * every tensor lands exactly on the file's byte count, then every type was
 * sized correctly and the VRAM figure downstream is trustworthy. If it does
 * not, something is wrong — a fork's private quantisation, a removed type, a
 * truncated download — and the user is told rather than shown a confident
 * wrong number.
 *
 * Verified delta = 0 against real Q4_K_M, Q6_K and IQ4_XS models.
 */
export function reconcile(expected: number, actual: number | null, dataStart: number): Reconciliation {
  if (actual === null) {
    return {
      status: 'unknown',
      expected,
      actual: null,
      delta: null,
      message: 'The true file size could not be read, so the tensor table could not be reconciled against it.',
    };
  }

  const delta = expected - actual;
  if (delta === 0) {
    return {
      status: 'verified',
      expected,
      actual,
      delta,
      message:
        'Verified: every tensor in the table, sized with llama.cpp\'s own block sizes, sums to exactly the file\'s byte count. Nothing was guessed.',
    };
  }

  // A file that stops right where the weights would begin is a header-only
  // fixture, not a corrupt download — the bundled sample is exactly this.
  if (actual === dataStart) {
    return {
      status: 'header-only',
      expected,
      actual,
      delta,
      message:
        'This file contains a complete header and tensor table but no weight data — a header-only fixture. Every figure below is still exact, because they all come from the table.',
    };
  }

  if (actual < expected) {
    return {
      status: 'truncated',
      expected,
      actual,
      delta,
      message: `The file is ${fmtDelta(delta)} smaller than its own tensor table describes, so it is incomplete. If you downloaded it, the download was cut short — the model will not load.`,
    };
  }

  return {
    status: 'mismatch',
    expected,
    actual,
    delta,
    message: `The tensor table sums to ${fmtDelta(-delta)} less than the file's real size. That usually means a quantisation type from a llama.cpp fork, whose block layout this tool does not know. Treat the memory figures as unreliable for this file.`,
  };
}

function fmtDelta(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1024 * 1024) return `${(abs / (1024 * 1024)).toFixed(1)} MiB`;
  if (abs >= 1024) return `${(abs / 1024).toFixed(1)} KiB`;
  return `${abs} bytes`;
}
